import React from 'react';
import { Chaperone, ChaperoneStep } from 'chaperone';
import * as scope from 'formydable';
import * as StringCode from './StringCodes';
import * as ActualCode from './ActualExampleCodes';
import * as TourGuides from './TourGuides';
import { SectionWithCode, Note } from 'Templates';
import { SpeechSynthesis } from 'Common/Utils';

const commonChaperoneSetting = {
	hideEvidence: false,
	neumorphism: false,
	placement: 'top-right',
	background: {
		backdropStyle: { opacity: 0.3 },
		highlightStyle: { backgroundColor: 'rgba(255,255,255, 0.2)' },
	},
	onLoad: ({ dialog }) => {
		const text = dialog.children[0].textContent;
		SpeechSynthesis.speak({ text });
	},
	onUnLoad: () => SpeechSynthesis.cancel(),
};

const GettingStartedChaperoneSetting = {
	...commonChaperoneSetting,
	onLoad: ({ dialog, index }) => {
		const text =
			index === 0
				? `Yow, if you haven't check the help in Getting Started yet. You will loss in this one. click here to go check it out.`
				: dialog.children[0].textContent;
		SpeechSynthesis.speak({ text });
	},
};

const AdvanceChaperoneSetting = {
	...commonChaperoneSetting,
	onLoad: ({ dialog, index }) => {
		const text =
			index === 0
				? `Hi, if you haven't check the guide in
		Advance Design Pattern yet. You will loss in this one.
		click here to go
		check it out.`
				: dialog.children[0].textContent;
		SpeechSynthesis.speak({ text });
	},
};

export const Installation = () => (
	<SectionWithCode
		readonly
		title='Installation'
		code={`npm i formydable`}
		language='js'
		theme={{
			plain: {
				color: 'rgb(215,215,215)',
				backgroundColor: 'rgb(86,86,86)',
			},
			styles: [],
		}}
	/>
);

export const Importing = () => (
	<SectionWithCode
		readonly
		title='Imports'
		code='import { useForm, useFormSupplier, FormSupplier, Validator, DoneTypingEvent } from "formydable"'
	/>
);
export const GettingStarted = ({ id = 'getting-started' }) => (
	<Chaperone>
		<ChaperoneStep
			{...commonChaperoneSetting}
			id={id}
			propertyMutation={TourGuides.GettingStarted}>
			<SectionWithCode
				triggerId={id}
				title='Getting Started'
				context='This is a react form validator that is delightfully made by mytabowrks
			which is blazingly fast and can be easily use and implement in any field
			components with ease.'
				code={StringCode.BasicCode}
				scope={scope}
			/>
		</ChaperoneStep>
		<Note
			variant='info'
			title='Tip'
			context={
				<>
					Rules validation order is from left to right. it will validate firstly
					the one on the left side which is the most priority. commonly{' '}
					<b>required</b> rule.
				</>
			}
			style={{ marginTop: '-20px', marginBottom: '20px' }}
		/>
	</Chaperone>
);

export const ControllableFields = ({ id = 'controllable-fields' }) => (
	<Chaperone>
		<ChaperoneStep
			{...GettingStartedChaperoneSetting}
			id={id}
			propertyMutation={TourGuides.ControllableFields}>
			<SectionWithCode
				triggerId={id}
				triggerIndex={localStorage.getItem('f-gstar') ? 1 : 0}
				title='Controllable Fields'
				context='you can make controllable fields without a sweat. just add a single line and you are done.'
				code={StringCode.Controllable}
				scope={scope}
			/>
		</ChaperoneStep>
	</Chaperone>
);
export const CustomizeMessages = ({ id = 'customize-messages' }) => (
	<Chaperone>
		<ChaperoneStep
			{...GettingStartedChaperoneSetting}
			id={id}
			propertyMutation={TourGuides.CustomizeMessages}>
			<SectionWithCode
				triggerId={id}
				triggerIndex={localStorage.getItem('f-gstar') ? 1 : 0}
				title='Customize Messages'
				context='you can make your own static rule messages by using "failedIn" without affecting the rules messages model.'
				code={StringCode.CustomMessages}
				scope={scope}
			/>
		</ChaperoneStep>
		<Note
			variant='info'
			context={
				<>
					Remember setting a <b>schema</b> only works once and will never change again after, 
					because that is how <b>react</b> <b>useState</b> works. but if you want to update constantly the schema or the default value
					like when you are updating information, you can <b>unmount</b> the component(which you put the
					{' '}<b>useForm</b>) after submit then <b>mount</b> it again when you are about to update information...
				</>
			}
			style={{ 
				marginTop: '-20px', 
				marginBottom: '20px'
			}}
		/>
	</Chaperone>
);

export const SubmittingForm = ({ id = 'submitting-form' }) => (
	<Chaperone>
		<ChaperoneStep
			{...GettingStartedChaperoneSetting}
			id={id}
			propertyMutation={TourGuides.SubmittingForm}>
			<SectionWithCode
				triggerId={id}
				triggerIndex={localStorage.getItem('f-gstar') ? 1 : 0}
				title='Submitting Form'
				context='form only exist to submit that is why it is not complete without submitting it on server side.'
				code={StringCode.WithSubmitCode}
				scope={{
					...scope,
					MockRequest: ActualCode.MockRequest,
					Alert: ActualCode.Alerto,
				}}
			/>
		</ChaperoneStep>
		<Note
			variant='info'
			context={
				<>
					Remember setting a <b>schema</b> only works once and will never change again after, 
					because that is how <b>react</b> <b>useState</b> works. but if you want to update constantly the schema or the default value
					like when you are updating information, you can <b>unmount</b> the component(which you put the
					{' '}<b>useForm</b>) after submit then <b>mount</b> it again when you are about to update information...
				</>
			}
			style={{ marginTop: '-20px' }}
		/>
		<Note
			variant='warning'
			context={
				<>
					The fields which are not registered from the schema are excluded from{' '}
					<b>FormEvent</b> method <b>json</b>, <b>param</b> and{' '}
					<b>paramArray</b>.
				</>
			}
		/>
		<Note
			variant='info'
			context={
				<>
					If you want to involve the unregistered field. You can always use{' '}
					<b>FormEvent</b> method <b>formData</b> that will return FormData
					instance.
				</>
			}
			style={{ marginBottom: '20px' }}
		/>
	</Chaperone>
);

export const AdvanceDesignPattern = ({ id = 'advance-design-pattern' }) => (
	<Chaperone>
		<ChaperoneStep
			{...commonChaperoneSetting}
			id={id}
			propertyMutation={TourGuides.AdvanceDesignPattern}>
			<SectionWithCode
				triggerId={id}
				title='Advance Design Pattern'
				context='We use FormSupplier to provide formState, formUpdate and formRegistry to children components, and we use useFormSupplier to get FormSupplier provided to children components'
				code={StringCode.AdvanceCode}
				scope={{
					...scope,
					MockRequest: ActualCode.MockRequest,
					Alert: ActualCode.Alerto,
				}}
			/>
		</ChaperoneStep>
	</Chaperone>
);

export const AliasedRegistry = ({ id = 'aliased-registry' }) => (
	<Chaperone>
		<ChaperoneStep
			{...AdvanceChaperoneSetting}
			id={id}
			propertyMutation={TourGuides.AliasedRegistry}>
			<SectionWithCode
				triggerId={id}
				title='Aliased Registry'
				context='It is inevitable that some form fields are multiple with the same name on it, the problem is we wanted to add those new or other fields into form state registry. for that reason formydable use alias registry and make a counter measure about those stuffs.'
				code={StringCode.AliasRegistry}
				scope={{
					...scope,
					FormWrapper: ActualCode.FormWrapper,
					MockSubmit: ActualCode.MockSubmit,
				}}
			/>
		</ChaperoneStep>
	</Chaperone>
);

export const FormUpdateWithDoneTypingEvent = ({
	id = 'with-donetypingevent',
}) => (
	<Chaperone>
		<ChaperoneStep
			{...AdvanceChaperoneSetting}
			id={id}
			propertyMutation={TourGuides.FormUpdateWithDoneTypingEvent}>
			<SectionWithCode
				triggerId={id}
				title='FormUpdate With DoneTypingEvent'
				context='It is use to fire the event after user is done typing, that will save a lot of unessesary execution of state changes while typing.'
				code={StringCode.DoneTypingCode}
				scope={{
					...scope,
					FormWrapper: ActualCode.FormWrapper,
					MockSubmit: ActualCode.MockSubmit,
					InputWithRules: ActualCode.InputWithRules,
				}}
			/>
		</ChaperoneStep>
	</Chaperone>
);

export const CustomizeRules = ({ id = 'customize-rules' }) => (
	<Chaperone>
		<ChaperoneStep
			{...AdvanceChaperoneSetting}
			id={id}
			propertyMutation={TourGuides.CustomizeRules}>
			<SectionWithCode
				triggerId={id}
				title='Customize Rules'
				context='you can make your own rules.'
				code={StringCode.CustomRulesCode}
				scope={{ ...scope, ...ActualCode }}
			/>
		</ChaperoneStep>
		<Note
			variant='info'
			title='Tip'
			context={<>Registering customize rules is once and for all.</>}
			style={{ marginTop: '-20px', marginBottom: '20px' }}
		/>
	</Chaperone>
);

export const AliasedRuleParameters = ({ id = 'aliased-rule-param' }) => (
	<Chaperone>
		<ChaperoneStep
			{...AdvanceChaperoneSetting}
			id={id}
			propertyMutation={TourGuides.AliasedRuleParameters}>
			<SectionWithCode
				triggerId={id}
				title='Aliased Rule Parameters'
				context='developer`s perspective is different with user`s. that is why you can always use aliases (@) to make user`s apprehend the messages'
				code={StringCode.AliasRules}
				scope={{ ...scope, ...ActualCode }}
			/>
		</ChaperoneStep>
	</Chaperone>
);

export const UpdatingRulesMessages = ({ id = 'updating-rules-messages' }) => (
	<Chaperone>
		<ChaperoneStep
			{...AdvanceChaperoneSetting}
			id={id}
			propertyMutation={TourGuides.UpdatingRulesMessages}>
			<SectionWithCode
				triggerId={id}
				title='Updating Rules Messages'
				context='if you don`t like the default rules messages you can always do this to change it permanently.'
				code={StringCode.RulesUpdateMessage}
				scope={{ ...scope, ...ActualCode }}
			/>
		</ChaperoneStep>
	</Chaperone>
);

export const useForm = () => (
	<SectionWithCode
		readonly
		title='useForm types'
		context='it is use to register schema and to get formState, formUpdate, formSubmit, formRegistry.'
		code='useForm(registry?: { [name: string]: { label: string, rules: string, value?: string|number, aliasOf?: string } }): { formState, formUpdate, formSubmit, formRegistry }'
	/>
);

export const formState = () => (
	<SectionWithCode
		readonly
		title='formState types'
		context='it is use to fetch the field state.'
		code='formState(fetch: string|array): { label: string, rules: string, isInvalid: boolean, isValidated: boolean, failedIn: null|string,  message: null|string } |  [name: string]: { label: string, rules: string, isInvalid: boolean, isValidated: boolean, failedIn: null|string,  message: null|string }'
	/>
);

export const formUpdate = () => (
	<SectionWithCode
		readonly
		title='formUpdate types'
		context='it is use to update the field state.'
		code='formUpdate({ target: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement }, alias?: string): void'
	/>
);

export const formRegistry = () => (
	<SectionWithCode
		readonly
		title='formRegistry types'
		context='it is use to remotely register the field rules into the schema.'
		code='formRegistry({ name: string, label: string, rules: string, value?: string|number, alias?: string }): void'
	/>
);

export const formSubmit = () => (
	<SectionWithCode
		readonly
		title='formSubmit types'
		context='it is use to handle the submission of form field.'
		code='formSubmit((event: FormEvent) => void): void'
	/>
);

export const DoneTypingEvent = () => (
	<SectionWithCode
		readonly
		title='DoneTypingEvent types'
		context='it is use to fire the event after user done typing, that will save a lot of unessesary execution while typing.'
		code='DoneTypingEvent((event) => void, delay: number): void'
	/>
);

export const rulesExtend = () => (
	<SectionWithCode
		readonly
		title='Validator.rulesExtend types'
		context='it is use to make your own rules.'
		code='Validator.rulesExtend({ [rule: string]: { exe(received: string, param1?: string, param2?: string): boolean, message: string } })'
	/>
);

export const rulesUpdateMessage = () => (
	<SectionWithCode
		readonly
		title='Validator.rulesUpdateMessage types'
		context='it is use to fire the event after user done typing, that will save a lot of unessesary execution while typing.'
		code='Validator.rulesUpdateMessage(rule_name: string, message: string|object)'
	/>
);

export const FormSupplier = () => (
	<SectionWithCode
		readonly
		title='FormSupplier types'
		context='it is use to set the useForm functions into useFormSupplier.'
		code='FormSupplier: FunctionComponent<{ value: { formState, formUpdate, formRegistry}}>'
	/>
);

export const useFormSupplier = () => (
	<SectionWithCode
		readonly
		title='useFormSupplier types'
		context='it is use to fetch FormSupplier provided.'
		code='useFormSupplier = (): { formState, formUpdate, formRegistry }'
	/>
);

export const Issues = () => {
	// React.useEffect(() => {
	// 	const url = "https://api.github.com/repos/mytabworks/formydable/issues";
	// 	fetch(url)
	// 	.then((response) => response.json())
	// 	.then((data) => console.log(data))
	// 	.catch((error) => console.error(error))
	// 	return () => {
			
	// 	}
	// }, [])
	return <></>
}